import { FC, MouseEventHandler, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DruidWebchatContext, { DruidWebchatContextType } from "../WebChatContext";
import React from "react";
import { useMergedOptions } from "../selectors/useMergedOptions";
import { FaReply } from "react-icons/fa";
import { useActivities, useCreateActivityRenderer } from 'botframework-webchat-api/lib/hooks';
import { useFocus, useScrollTo } from 'botframework-webchat-component/lib/hooks';

import ReactDOMServer from "react-dom/server";
import { createPortal } from "react-dom";
import classNames from "classnames";


const DruidActivityWrapper =  ((props: PropsWithChildren<{ activity: any }>) => {
    const { 
      UI: { activityWrapperClassEnabled, replyToMessageEnabled } = {},
      styleOptions: { paddingRegular, subtle, accent, bubbleBorderRadius, bubbleNubOffset, sendBoxBackground } = {}
    } = useMergedOptions();

    const [druidWebChatState, druidWebChatDispatch , ,druidStyleToEmotionObject] = useContext<DruidWebchatContextType>(DruidWebchatContext);
    const { inputBlocked } = druidWebChatState;
    const activityRenderer =  useCreateActivityRenderer();
    const [activities] = useActivities();
    const scrollTo = useScrollTo();
    const focus = useFocus();

    const containerRef = useRef<HTMLDivElement>(null);
    const [portalNode, setPortalNode] = useState<Element | null>(null);
   
    const { children, activity } = props;
    const { channelData = { flowStepName: undefined, flowStepType: undefined } }: {channelData: { flowStepName?: string, flowStepType?: string } } = activity;

    const handleJumpToMessage = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if(activity?.channelData?.isRepliedToActivity) {
          event.preventDefault();
          scrollTo({ activityID: activity.id } as any, { behavior: 'smooth'})
        }
      },
      [activity]
    );

    const handleReplyTo = useCallback(
      activity => () => {
        focus('sendBoxWithoutKeyboard');
        druidWebChatDispatch({ type: 'SET_SELECTEDACTIVITY_FOR_REPLY', payload: { activity}});
      },
      [activity]
    );
    useEffect(() => {
      if(replyToMessageEnabled) {
        setPortalNode(containerRef.current?.querySelector(".webchat__bubble__content") || null);
      }
    }, [containerRef, replyToMessageEnabled]);

    const shouldWrapActivityWithFlowStepName = !!channelData.flowStepName && !!channelData.flowStepType && !!activityWrapperClassEnabled;

    const replyToActivity = useMemo(() => {
      const { channelData: { replyActivityId = null } = {} } = activity;
      const replyActivity = activities.find(act => act.id && act.id === replyActivityId || act.channelData?.routedMessageId === replyActivityId )
      if(replyActivity?.from) {
        const toReturnActivity = JSON.parse(JSON.stringify(replyActivity));
        if(toReturnActivity.attachmentLayout === 'carousel') {
          toReturnActivity.attachments = null
        }
        toReturnActivity.channelData ||= {}
        toReturnActivity.channelData.replyActivityId = undefined;
        toReturnActivity.channelData.isRepliedToActivity = true;
        toReturnActivity.text ||= "";
        toReturnActivity.text = toReturnActivity.text.substring(0, 200) + (toReturnActivity.text.length > 200 ? "..." : "");
        return toReturnActivity
      }
      return replyActivity;
    }, [activity?.id]);
    // const renderReplyToActivity = replyToActivity ? activityRenderer({ activity: replyToActivity, nextVisibleActivity: {} as any}) : null;
    const DefaultActivityRenderer: any  = useMemo(() => replyToActivity ? activityRenderer({ activity: replyToActivity, nextVisibleActivity: {} as any}) : null, [replyToActivity]);

    const withReplyActivity = replyToActivity && !!DefaultActivityRenderer && portalNode;

    const ROOT_STYLE = {
      '&.druid_webchat_activity-wrapper': {
        '&:hover .druid__webchat__reply-button':  {
              display: 'block !important'
        },
        'button.druid__webchat__reply-button': {
            background: sendBoxBackground,
            border: 'none',
            position: 'absolute',
            display: 'none',
            ...(activity?.from?.role === 'bot' ? { left: paddingRegular } : { right: 0} ),
            // top: `calc(50% - ${paddingRegular}px)`,
            ...(bubbleNubOffset === 'bottom' ? { top: paddingRegular } : { bottom: paddingRegular}),
            transform: 'translate(-50%, -50%)',
            padding: 0,
            width: '25px',
            height: '25px',
            'svg': {
              width: '1rem',
              height: '1rem',
              fill: subtle,
              verticalAlign: 'middle',
            },
            '&:not([aria-disabled="true"]) svg:hover': {
              fill: accent
            },
        },
        '&.druid_webchat_activity-wrapper-with-reply': {
          '.webchat__bubble__content': {
            display: 'flex',
            flexDirection: 'column-reverse',
            '& .druid_webchat_activity-wrapper': {
              marginTop: paddingRegular! / 2,
              '.webchat__bubble__nub-pad, .webchat__stacked-layout__alignment-pad': {
                display: 'none',
              },
              '.webchat__bubble__content': {
                ...(replyToActivity?.from?.role === 'bot' && { borderRadius: `${bubbleBorderRadius}px !important`, borderTopLeftRadius: `0 !important`}), 
                backgroundColor: 'white !important',
              }
            }
          }
        }
      }
    };

    const rootClassName = druidStyleToEmotionObject(ROOT_STYLE) + '';


    return <div  className={classNames({
        "druid_webchat_activity-wrapper": true, 
        "druid_webchat_activity-wrapper-with-reply": withReplyActivity,
        [`druid-activity_${channelData?.flowStepName?.trim().replace(/[^a-zA-Z0-9]/g, "")}_${channelData?.flowStepType?.trim().replace(/[^a-zA-Z0-9]/g, "-")}`]: shouldWrapActivityWithFlowStepName,
        [rootClassName]: true
      })}
      //  onClick={replyToMessageEnabled && withReplyActivity ? handleJumpToMessage : undefined}
       onClick={handleJumpToMessage}
       ref={containerRef}>
        {replyToMessageEnabled && withReplyActivity && createPortal(
            <DefaultActivityRenderer></DefaultActivityRenderer>,
            portalNode
        )}
        { children }
        {
           replyToMessageEnabled && !inputBlocked && !activity?.channelData?.isRepliedToActivity &&
              <button className="druid__webchat__reply-button"
                aria-disabled={false} onClick={handleReplyTo(activity)} title={'Reply'}>
                <FaReply></FaReply>
              </button>
        }
      </div>;

    // return <React.Fragment>{ children }</React.Fragment>;
});

export default DruidActivityWrapper;